import { indexes } from '@mab-common/utils'

import type {
  MsearchRequest,
  MsearchRequestItem,
  QueryDslQueryContainer,
} from '@elastic/elasticsearch/lib/api/types'

import {
  buildElasticResultSourceFields,
  buildElasticSortFields,
} from '~/services/elastic/builder'
import { useElasticQueryMultiMap } from '~/services/query/use-elastic-query'
import EsIndexTypes = indexes.EsIndexTypes
import RecordTypes = indexes.RecordTypes

export default function useRelatedArtworks(
  artistId: string,
  formatId?: string,
  artworkId?: string,
  excludeCollection?: string,
  year?: number,
  fallbackData?: any
) {
  const filterQueries: QueryDslQueryContainer[] = [
    {
      term: {
        type: RecordTypes.ARTWORK,
      },
    },
    {
      term: {
        artistId: artistId,
      },
    },
  ]

  const mustNotQueries: QueryDslQueryContainer[] = [
    {
      term: {
        isVisible: false,
      },
    },
  ]
  if (artworkId) {
    mustNotQueries.push({
      term: {
        objectID: artworkId,
      },
    })
  }
  if (excludeCollection) {
    mustNotQueries.push({
      term: {
        'collectionId.keyword': excludeCollection,
      },
    })
  }

  const commonQuery: MsearchRequestItem = {
    from: 0,
    size: 12,
    sort: buildElasticSortFields(RecordTypes.ARTWORK, true),
    _source: buildElasticResultSourceFields(RecordTypes.ARTWORK),
  }

  const msearch: MsearchRequest = {
    index: EsIndexTypes.ARTWORKS,
    searches: [
      {},
      {
        ...commonQuery,
        query: {
          bool: {
            filter: filterQueries,
            must_not: mustNotQueries,
          },
        },
      },
    ],
  }

  const relatedFilterPriority = (): QueryDslQueryContainer[] => {
    if (!year) return []
    const minYear = year - 5
    const maxYear = year + 5
    return [
      {
        range: {
          year: {
            // `,format._id:${formatId},year > ${minYear},year < ${maxYear}` //maxNumber is defined here,
            // but hits per page are set in Algolia (currently to 12)
            // converted the top algolia query with (or equal)
            gte: minYear,
            lte: maxYear,
          },
        },
      },
      {
        term: {
          'format._id.keyword': formatId,
        },
      },
    ]
  }

  const relatedFilterFormat = (): QueryDslQueryContainer[] => {
    return [
      {
        term: {
          'format._id.keyword': formatId,
        },
      },
    ]
  }

  if (year && formatId && artistId) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    msearch.searches.push(
      ...[
        {
          index: EsIndexTypes.ARTWORKS,
        },
        {
          ...commonQuery,
          query: {
            bool: {
              filter: [...filterQueries, ...relatedFilterPriority()],
              must_not: mustNotQueries,
            },
          },
        },
      ]
    )
  }

  if (formatId && artistId) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    msearch.searches.push(
      ...[
        {
          index: EsIndexTypes.ARTWORKS,
        },
        {
          ...commonQuery,
          query: {
            bool: {
              filter: [...filterQueries, ...relatedFilterFormat()],
              must_not: mustNotQueries,
            },
          },
        },
      ]
    )
  }

  return useElasticQueryMultiMap(
    [
      {
        recordType: RecordTypes.ARTWORK,
        name: 'relatedArtwork',
      },
      {
        recordType: RecordTypes.ARTWORK,
        name: 'relatedPriority',
      },
      {
        recordType: RecordTypes.ARTWORK,
        name: 'relatedFormat',
      },
    ],
    msearch
  )
}
